/**
 * fixedInstrumentCalc.js — Estimate current value for fixed instruments
 * (EPF, PPF, VPF, FD, RD) based on invested amount + interest compounding.
 *
 * These instruments don't have live market prices, so we compute
 * an estimated current value for dashboard display only.
 */

const DEFAULT_RATES = {
  EPF: 8.25,
  epf: 8.25,
  PPF: 7.1,
  ppf: 7.1,
  vpf: 8.25,
  VPF: 8.25,
  FD: 7.0,
  fd: 7.0,
  RD: 7.0,
  rd: 7.0,
};

const FIXED_TYPES = new Set(Object.keys(DEFAULT_RATES));

/**
 * Check if a holding is a fixed instrument type
 */
export const isFixedInstrument = (assetType) => {
  return FIXED_TYPES.has(assetType);
};

/**
 * Calculate estimated current value for a fixed instrument.
 *
 * @param {number} investedAmount - principal / invested value
 * @param {number} annualRate - interest rate in % (e.g. 8.25)
 * @param {string} startDate - ISO date string of when investment started
 * @param {string} compounding - 'annual' or 'quarterly'
 * @returns {number} estimated current value
 */
export const calculateFixedInstrumentValue = (
  investedAmount,
  annualRate,
  startDate,
  compounding = 'annual'
) => {
  if (!investedAmount || investedAmount <= 0 || !startDate) return investedAmount || 0;

  const start = new Date(startDate);
  const now = new Date();
  const yearsElapsed = (now - start) / (365.25 * 24 * 60 * 60 * 1000);

  if (yearsElapsed <= 0) return investedAmount;

  const r = annualRate / 100;

  if (compounding === 'quarterly') {
    // Quarterly compounding: A = P * (1 + r/4)^(4*t)
    const n = 4;
    return investedAmount * Math.pow(1 + r / n, n * yearsElapsed);
  }

  // Annual compounding: A = P * (1 + r)^t
  return investedAmount * Math.pow(1 + r, yearsElapsed);
};

/**
 * Get the compounding type for an asset type
 */
const getCompounding = (assetType) => {
  const type = (assetType || '').toUpperCase();
  if (type === 'FD' || type === 'RD') return 'quarterly';
  return 'annual'; // EPF, PPF, VPF
};

/**
 * Get the display label for the interest rate
 */
export const getFixedRateLabel = (assetType, rate) => {
  return `Est. @${rate}% p.a.`;
};

/**
 * Apply estimated current values to an array of holdings.
 * Only modifies holdings that are fixed instruments AND have no current_price.
 * Returns a new array (does not mutate original).
 *
 * @param {Array} holdings
 * @returns {Array} holdings with estimated values applied
 */
export const applyFixedInstrumentValues = (holdings) => {
  if (!holdings || holdings.length === 0) return holdings;

  return holdings.map((h) => {
    const type = h.asset_type || h.investment_type;
    if (!isFixedInstrument(type)) return h;

    // Skip if already has a meaningful current value that differs from invested
    // (means it was manually set or updated)
    if (h.current_value && h.invested_value && h.current_value !== h.invested_value) {
      return h;
    }

    const investedAmount = h.invested_value || h.current_value || 0;
    if (investedAmount <= 0) return h;

    const rate = h.interest_rate || DEFAULT_RATES[type] || 7.0;
    const startDate = h.start_date || h.created_at;
    const compounding = getCompounding(type);

    const estimatedValue = calculateFixedInstrumentValue(
      investedAmount,
      rate,
      startDate,
      compounding
    );

    return {
      ...h,
      current_value: Math.round(estimatedValue),
      _estimatedRate: rate,
      _isEstimated: true,
    };
  });
};
